import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AcademicStructureManager = ({ userRole, userName, onClose }) => {
  const [activeTab, setActiveTab] = useState('subjects');
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [questionPapers, setQuestionPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [filters, setFilters] = useState({
    subject: '',
    class: '',
    chapter: ''
  });

  const [newItem, setNewItem] = useState({
    name: '',
    code: '',
    description: '',
    subject: '',
    class: '',
    chapter: '',
    order: 1
  });

  useEffect(() => {
    fetchData();
  }, [activeTab, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case 'subjects':
          const subjectsRes = await axios.get('/api/subjects');
          setSubjects(subjectsRes.data);
          break;
        case 'chapters':
          const chaptersRes = await axios.get('/api/chapters', { params: filters });
          setChapters(chaptersRes.data);
          break;
        case 'topics':
          const topicsRes = await axios.get('/api/topics', { params: filters });
          setTopics(topicsRes.data);
          break;
        case 'papers':
          const papersRes = await axios.get('/api/question-papers', { params: filters });
          setQuestionPapers(papersRes.data);
          break;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const endpoint = `/api/${activeTab === 'papers' ? 'question-papers' : activeTab}`;
      const data = { ...newItem, createdBy: userName, userRole };
      
      await axios.post(endpoint, data);
      setNewItem({ name: '', code: '', description: '', subject: '', class: '', chapter: '', order: 1 });
      setShowAddForm(false);
      fetchData();
      alert(`${activeTab.slice(0, -1)} added successfully!`);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleBulkUpload = async (jsonData) => {
    try {
      const endpoint = `/api/${activeTab === 'papers' ? 'question-papers' : activeTab}/bulk-upload`;
      const response = await axios.post(endpoint, {
        [activeTab]: jsonData,
        createdBy: userName,
        userRole
      });
      
      alert(response.data.message);
      setShowBulkUpload(false);
      fetchData();
    } catch (error) {
      alert('Bulk upload error: ' + (error.response?.data?.error || error.message));
    }
  };

  const renderSubjects = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h3>📚 Subject Management</h3>
        <button onClick={() => setShowAddForm(true)} className="btn" style={{ background: '#28a745' }}>
          ➕ Add Subject
        </button>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
        {subjects.map(subject => (
          <div key={subject._id} style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            background: 'white'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{subject.name}</h4>
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
              <strong>Code:</strong> {subject.code}
            </p>
            {subject.description && (
              <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                {subject.description}
              </p>
            )}
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#999' }}>
              Created by: {subject.createdBy}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderChapters = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h3>📖 Chapter Management</h3>
        <button onClick={() => setShowAddForm(true)} className="btn" style={{ background: '#17a2b8' }}>
          ➕ Add Chapter
        </button>
      </div>
      
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <select 
          value={filters.subject} 
          onChange={(e) => setFilters({...filters, subject: e.target.value})}
          style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
        </select>
        
        <select 
          value={filters.class} 
          onChange={(e) => setFilters({...filters, class: e.target.value})}
          style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="">All Classes</option>
          {Array.from({length: 12}, (_, i) => (
            <option key={i+1} value={i+1}>Class {i+1}</option>
          ))}
        </select>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
        {chapters.map(chapter => (
          <div key={chapter._id} style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            background: 'white'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{chapter.name}</h4>
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
              <strong>Subject:</strong> {chapter.subject} | <strong>Class:</strong> {chapter.class}
            </p>
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
              <strong>Order:</strong> {chapter.order}
            </p>
            {chapter.description && (
              <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                {chapter.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderTopics = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h3>📝 Topic Management</h3>
        <button onClick={() => setShowAddForm(true)} className="btn" style={{ background: '#ffc107', color: '#000' }}>
          ➕ Add Topic
        </button>
      </div>
      
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <select 
          value={filters.subject} 
          onChange={(e) => setFilters({...filters, subject: e.target.value})}
          style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="">All Subjects</option>
          {subjects.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
        </select>
        
        <select 
          value={filters.class} 
          onChange={(e) => setFilters({...filters, class: e.target.value})}
          style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="">All Classes</option>
          {Array.from({length: 12}, (_, i) => (
            <option key={i+1} value={i+1}>Class {i+1}</option>
          ))}
        </select>
        
        <select 
          value={filters.chapter} 
          onChange={(e) => setFilters({...filters, chapter: e.target.value})}
          style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="">All Chapters</option>
          {chapters.filter(c => !filters.subject || c.subject === filters.subject)
                   .filter(c => !filters.class || c.class === filters.class)
                   .map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
        </select>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '15px' }}>
        {topics.map(topic => (
          <div key={topic._id} style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            background: 'white'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{topic.name}</h4>
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
              <strong>Subject:</strong> {topic.subject} | <strong>Class:</strong> {topic.class}
            </p>
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
              <strong>Chapter:</strong> {topic.chapter} | <strong>Order:</strong> {topic.order}
            </p>
            {topic.description && (
              <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                {topic.description}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderQuestionPapers = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h3>📋 Question Papers</h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setShowBulkUpload(true)} className="btn" style={{ background: '#6f42c1' }}>
            📤 Bulk Upload JSON
          </button>
          <button onClick={() => setShowAddForm(true)} className="btn" style={{ background: '#dc3545' }}>
            ➕ Add Paper
          </button>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '15px' }}>
        {questionPapers.map(paper => (
          <div key={paper._id} style={{ 
            padding: '15px', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            background: 'white'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{paper.title}</h4>
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
              <strong>Subject:</strong> {paper.subject} | <strong>Class:</strong> {paper.class}
            </p>
            {paper.chapter && (
              <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                <strong>Chapter:</strong> {paper.chapter}
              </p>
            )}
            {paper.topic && (
              <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                <strong>Topic:</strong> {paper.topic}
              </p>
            )}
            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
              <strong>Questions:</strong> {paper.totalQuestions}
            </p>
            {paper.assignedTeachers && paper.assignedTeachers.length > 0 && (
              <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                <strong>Assigned to:</strong> {paper.assignedTeachers.join(', ')}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (!['admin1', 'admin'].includes(userRole)) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Access Denied</h3>
        <p>Admin Level 1 access required for Academic Structure Management.</p>
        <button onClick={onClose} className="btn">Close</button>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100%', 
      height: '100%', 
      background: 'rgba(0,0,0,0.5)', 
      zIndex: 10000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '10px', 
        width: '95%', 
        maxWidth: '1200px', 
        height: '90%', 
        overflow: 'auto',
        position: 'relative'
      }}>
        <div style={{ 
          padding: '20px', 
          borderBottom: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f8f9fa'
        }}>
          <h2 style={{ margin: 0 }}>🏫 Academic Structure Manager</h2>
          <button onClick={onClose} style={{ 
            background: '#dc3545', 
            color: 'white', 
            border: 'none', 
            borderRadius: '50%', 
            width: '30px', 
            height: '30px', 
            cursor: 'pointer' 
          }}>×</button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            marginBottom: '20px',
            borderBottom: '1px solid #ddd',
            paddingBottom: '10px'
          }}>
            {[
              { key: 'subjects', label: '📚 Subjects', color: '#28a745' },
              { key: 'chapters', label: '📖 Chapters', color: '#17a2b8' },
              { key: 'topics', label: '📝 Topics', color: '#ffc107' },
              { key: 'papers', label: '📋 Papers', color: '#dc3545' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  background: activeTab === tab.key ? tab.color : '#f8f9fa',
                  color: activeTab === tab.key ? 'white' : '#333',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
          ) : (
            <>
              {activeTab === 'subjects' && renderSubjects()}
              {activeTab === 'chapters' && renderChapters()}
              {activeTab === 'topics' && renderTopics()}
              {activeTab === 'papers' && renderQuestionPapers()}
            </>
          )}
        </div>

        {showAddForm && (
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '10px', 
              width: '400px',
              maxHeight: '80%',
              overflow: 'auto'
            }}>
              <h3>Add New {activeTab.slice(0, -1)}</h3>
              <form onSubmit={handleAdd}>
                <div style={{ marginBottom: '15px' }}>
                  <label>Name:</label>
                  <input
                    type="text"
                    value={newItem.name}
                    onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                    required
                    style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                  />
                </div>

                {activeTab === 'subjects' && (
                  <div style={{ marginBottom: '15px' }}>
                    <label>Code:</label>
                    <input
                      type="text"
                      value={newItem.code}
                      onChange={(e) => setNewItem({...newItem, code: e.target.value})}
                      required
                      style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    />
                  </div>
                )}

                {activeTab !== 'subjects' && (
                  <>
                    <div style={{ marginBottom: '15px' }}>
                      <label>Subject:</label>
                      <select
                        value={newItem.subject}
                        onChange={(e) => setNewItem({...newItem, subject: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                      >
                        <option value="">Select Subject</option>
                        {subjects.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
                      </select>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                      <label>Class:</label>
                      <select
                        value={newItem.class}
                        onChange={(e) => setNewItem({...newItem, class: e.target.value})}
                        required
                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                      >
                        <option value="">Select Class</option>
                        {Array.from({length: 12}, (_, i) => (
                          <option key={i+1} value={i+1}>Class {i+1}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {activeTab === 'topics' && (
                  <div style={{ marginBottom: '15px' }}>
                    <label>Chapter:</label>
                    <select
                      value={newItem.chapter}
                      onChange={(e) => setNewItem({...newItem, chapter: e.target.value})}
                      required
                      style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                    >
                      <option value="">Select Chapter</option>
                      {chapters.filter(c => c.subject === newItem.subject && c.class === newItem.class)
                               .map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                )}

                <div style={{ marginBottom: '15px' }}>
                  <label>Description:</label>
                  <textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                    style={{ width: '100%', padding: '8px', marginTop: '5px', minHeight: '60px' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setShowAddForm(false)} className="btn">
                    Cancel
                  </button>
                  <button type="submit" className="btn" style={{ background: '#28a745' }}>
                    Add
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showBulkUpload && (
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{ 
              background: 'white', 
              padding: '20px', 
              borderRadius: '10px', 
              width: '500px',
              maxHeight: '80%',
              overflow: 'auto'
            }}>
              <h3>Bulk Upload JSON</h3>
              <p>Upload multiple question papers with questions in JSON format.</p>
              
              <div style={{ marginBottom: '15px' }}>
                <label>JSON Data:</label>
                <textarea
                  placeholder='[{"title":"Sample Paper","subject":"Math","class":"5","questions":[{"question":"What is 2+2?","options":["3","4","5","6"],"correctAnswer":"4","difficulty":"easy"}]}]'
                  style={{ width: '100%', height: '200px', padding: '8px', marginTop: '5px', fontFamily: 'monospace' }}
                  id="bulkJsonData"
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowBulkUpload(false)} className="btn">
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    try {
                      const jsonData = JSON.parse(document.getElementById('bulkJsonData').value);
                      handleBulkUpload(jsonData);
                    } catch (error) {
                      alert('Invalid JSON format');
                    }
                  }}
                  className="btn" 
                  style={{ background: '#6f42c1' }}
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcademicStructureManager;